import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  FaSearch, FaUtensils, FaMusic, FaCar, FaCamera,
  FaWineGlassAlt, FaPaintBrush, FaStar, FaMapMarkerAlt,
  FaCut, FaSuitcase, FaShoePrints, FaCalendarAlt,
  FaMicrophone, FaHeart, FaRing, FaHotel, FaGem,
  FaTshirt, FaChair, FaUsers, FaGift, FaBirthdayCake, FaGlassCheers
} from 'react-icons/fa';
import { spApi } from '../../api/serviceProviders/spApi';
import styles from '../styles/WatoaHuduma.module.css';
import SpinnerLoader from '../../components/Buttons/SpinnerLoader';
import WatoaHudumaModal from '../../modals/watoaHudumaModal/WatoaHudumaModal';

// Map category names to icons
const getCategoryIcon = (categoryName) => {
  const iconMap = {
    'usafiri': <FaCar />,
    'vinywaji': <FaWineGlassAlt />,
    'magauri_ya_harusi': <FaGlassCheers />,
    'pochi': <FaGift />,
    'keki': <FaBirthdayCake />,
    'zawadi_gift_shop': <FaGift />,
    'tarumbeta': <FaMusic />,
    'saluni': <FaCut />,
    'suti': <FaSuitcase />,
    'viatu': <FaShoePrints />,
    'ndafu': <FaStar />,
    'stationery': <FaPaintBrush />,
    'planner': <FaCalendarAlt />,
    'mshereheshaji': <FaMicrophone />,
    'honeymoon': <FaHeart />,
    'chakula': <FaUtensils />,
    'pete': <FaRing />,
    'hoteli': <FaHotel />,
    'vito_jewelry': <FaGem />,
    'nguo': <FaTshirt />,
    'picha': <FaCamera />,
    'ukumbi': <FaChair />,
    'maids': <FaUsers />,
    'mapambo': <FaPaintBrush />,
    'make-up': <FaPaintBrush />,
    'muziki': <FaMusic />
  };

  const key = categoryName?.toLowerCase().replace(/\s+/g, '_') || '';
  if (iconMap[key]) return iconMap[key];
  for (const [mapKey, icon] of Object.entries(iconMap)) {
    if (key.includes(mapKey)) return icon;
  }
  return <FaStar />;
};

const getCategoryColor = (index) => {
  const colors = [
    '#1e3a8a', '#7b1e7b', '#b45309', '#b91c1c', '#1e40af',
    '#047857', '#854d0e', '#0f766e', '#9d174d', '#0d9488',
    '#5b21b6', '#b45309', '#92400e', '#075985', '#9f1239'
  ];
  return colors[index % colors.length];
};

const WatoaHuduma = () => {
  const [serviceProviders, setServiceProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [modalTab, setModalTab] = useState('login');
  
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  // Debounced search logic equivalent
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setCurrentPage(1); // Reset to first page on new search
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchInput, searchTerm]);

  const fetchProvidersData = useCallback(async () => {
    try {
      if (!searchTerm) {
        setLoading(true);
      }

      // Fetch all providers (page 1) since we filter client-side and only need the first few
      const { providers } = await spApi.getProviders(1);
      
      let fetchedProviders = providers || [];
      // Randomize the providers for a dynamic display
      if (fetchedProviders.length > 0) {
        fetchedProviders = [...fetchedProviders].sort(() => 0.5 - Math.random());
      }
      
      setServiceProviders(fetchedProviders);
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchProvidersData();
  }, [fetchProvidersData]);

  // Filter providers based on search term
  const filteredProviders = serviceProviders.filter((provider) => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    const matchesName = provider.name?.toLowerCase().includes(term);
    const matchesCategorySearch = provider.categories?.some(category =>
      category.category?.toLowerCase().includes(term) ||
      category.name?.toLowerCase().includes(term)
    );
    const matchesLocation = provider.location?.toLowerCase().includes(term);

    return (matchesName || matchesCategorySearch || matchesLocation);
  });

  // Fetch categories only once
  useEffect(() => {
    const fetchCategories = async () => {
      const categoriesData = await spApi.getServiceCategories();
      if (categoriesData) setCategories(categoriesData);
    };
    fetchCategories();
  }, []);

  const handleModalClose = () => setShowModal(false);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      setCurrentPage(page);
    }
  };

  const categoryCards = categories.map((cat, index) => {
    const categoryName = cat.category || cat.name || '';
    const imageUrl = cat.photo?.sm_path || cat.photos?.sm_photo || cat.image || cat.photo?.md_path;

    return {
      id: cat.id,
      label: categoryName,
      icon: getCategoryIcon(categoryName),
      color: getCategoryColor(index),
      backgroundImage: imageUrl,
      originalData: cat
    };
  });

  return (
    <Container className={styles.container}>
      <br /><br /><br />

      {/* Stylish Heading & Buttons */}
      <div className="text-center mb-5">
        <h2 className="stylish-heading mb-4">Watoa Huduma</h2>
        <div className="d-flex justify-content-center gap-3 flex-wrap">
          <Button
            variant="outline-primary"
            onClick={() => { setModalTab('login'); setShowModal(true); }}
            style={{ borderRadius: '30px', padding: '8px 25px', fontWeight: '500' }}
          >
            Ingia (Service Provider)
          </Button>
          <Button
            variant="primary"
            onClick={() => { setModalTab('register'); setShowModal(true); }}
            style={{ borderRadius: '30px', padding: '8px 25px', fontWeight: '500' }}
          >
            Jisajili Kuwa Mtoa Huduma
          </Button>
        </div>
      </div>

      {/* Centered Search Section */}
      <Row className="justify-content-center mb-5">
        <Col xs={12} md={8} lg={6}>
          <div className={styles.centeredSearchContainer}>
            <div className={styles.searchWrapper}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Tafuta watoa huduma kwa jina, huduma, au eneo..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={styles.centeredSearchInput}
              />
              {searching && (
                <div style={{
                  position: 'absolute',
                  right: '20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6c757d',
                  fontSize: '0.85rem',
                  zIndex: 10
                }}>
                  Inatafuta...
                </div>
              )}
            </div>
            {searchInput ? (
              <p className={styles.searchHint} style={{ cursor: 'pointer', color: '#4169e1' }} onClick={handleClearSearch}>
                ✕ Ondoa utafutaji
              </p>
            ) : (
              <p className={styles.searchHint}>
                Tafuta watoa huduma unaowahitaji kwa ajili ya shughuli yako
              </p>
            )}
          </div>
        </Col>
      </Row>

      {/* Service Providers Cards */}
      {loading ? (
        <SpinnerLoader />
      ) : (
        <>
          <Row className={styles.eventsGrid}>
            {filteredProviders.length > 0 ? (
              filteredProviders.slice(0, 6).map((provider) => (
                <Col key={provider.id} xs={12} md={6} lg={4} className="mb-4">
                  <Card className={styles.eventCard}>
                    <Link to={`/watoa-huduma-details/${provider.id}`} state={{ provider }}>
                      <Card.Img
                        variant="top"
                        src={provider.wallpaper?.sm_photo || provider.wallpaper?.md_photo || provider.wallpaper || 'https://via.placeholder.com/300x200'}
                        alt={provider.name}
                        className={styles.cardImage}
                      />
                    </Link>
                    <Card.Body className={styles.cardBody}>
                      <Card.Title className={styles.cardTitle}>{provider.name}</Card.Title>

                      {/* Location */}
                      {provider.location && (
                        <div className={styles.cardLocation}>
                          <FaMapMarkerAlt className={styles.locationIcon} />
                          <span>{provider.location}</span>
                        </div>
                      )}

                      {/* Categories */}
                      <div className={styles.cardCategories}>
                        {provider.categories?.slice(0, 3).map((cat, idx) => {
                          const categoryName = cat.category || cat.name || '';
                          const categoryIcon = getCategoryIcon(categoryName);

                          return (
                            <span key={idx} className={styles.categoryTag}>
                              <span className={styles.tagIcon}>{categoryIcon}</span>
                              {categoryName}
                            </span>
                          );
                        })}
                        {provider.categories?.length > 3 && (
                          <span className={styles.categoryTag}>
                            +{provider.categories.length - 3}
                          </span>
                        )}
                      </div>

                      <div className={styles.buttonContainer}>
                        <Link to={`/watoa-huduma-details/${provider.id}`} state={{ provider }}>
                          <button className={styles.viewDetailsBtn}>
                            Angalia Huduma
                          </button>
                        </Link>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            ) : (
              <Col xs={12} className="text-center py-5">
                <h4>Hakuna watoa huduma waliopatikana</h4>
                <p className="text-muted">Jaribu kubadilisha vigezo vya utafutaji</p>
              </Col>
            )}
          </Row>
        </>
      )}

      {/* Category Cards */}
      {!loading && (
        <Row className="mb-5 justify-content-center">
          {categoryCards.map((category, index) => (
            <Col key={category.id} xs={6} sm={4} md={3} lg={2} className="mb-3">
              <Link to={`/watoa-huduma/category/${category.id}`} state={{ category: category.originalData }} style={{ textDecoration: 'none' }}>
                <Card className={styles.categoryCard}>
                  {category.backgroundImage && (
                    <div
                      className={styles.categoryBackground}
                      style={{
                        backgroundImage: `url(${category.backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.2
                      }}
                    />
                  )}
                  <Card.Body className="text-center p-3 position-relative">
                    <div className={styles.categoryIcon} style={{ color: category.color, position: 'relative', zIndex: 2 }}>
                      {category.icon}
                    </div>
                    <div className={styles.categoryLabel} style={{ position: 'relative', zIndex: 2 }}>
                      {category.label}
                    </div>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>
      )}

      <WatoaHudumaModal show={showModal} onHide={handleModalClose} categories={categories} defaultTab={modalTab} />
    </Container>
  );
};

export default WatoaHuduma;